import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ErrorDeApi, pedir } from '../api/cliente';
import { Boton, Encabezado, Tarjeta } from '../componentes/ui/basicos';
import { Selector } from '../componentes/ui/formulario';
import { useTextoDeError } from '../componentes/ui/estados';
import {
  BuscadorDeArticulos,
  ResumenDeVenta,
  SelectorDeCliente,
  SinTurno,
  conDosDecimales,
  importeVisible,
  type ClienteElegido,
} from '../venta/PiezasDeVenta';
import { calcularLineaSegura, useVenta, type MedioDePago } from '../venta/useVenta';
import { FormasDePago } from '../venta/FormasDePago';
import { usePermiso } from '../sesion/ProveedorDeSesion';

interface Puesto {
  readonly id: string;
  readonly nombre: string;
}

interface PuntoDeVenta {
  readonly id: string;
  readonly nombre: string;
  readonly numero: number;
}

interface Lista {
  readonly id: string;
  readonly nombre: string;
  readonly esPredeterminada: boolean;
  /** Sus tramos por cantidad (E36): la cuenta de la pantalla los aplica como el servidor. */
  readonly escalas: readonly { desdeCantidad: string; porcentaje: string }[];
}

interface TipoDeComprobante {
  readonly id: string;
  readonly nombre: string;
  readonly activo: boolean;
  readonly tocaCaja: boolean;
  readonly afectaCuenta: boolean;
  readonly familiaFiscal: string | null;
  readonly listaDePreciosId: string | null;
  readonly listaEsFija: boolean;
  readonly requiereOrigen: boolean;
  readonly puedeUsar: boolean;
  /** Si el tipo la fija, quien vende no la elige. */
  readonly puestoDeCajaId: string | null;
}

const MEDIOS: readonly MedioDePago[] = [
  'efectivo',
  'debito',
  'credito',
  'transferencia',
  'cuenta_corriente',
];

/**
 * Los signos van dibujados y no escritos.
 *
 * Un menos y un más no son texto que se traduzca, pero tampoco son literales sueltos en la
 * pantalla: como figuras quedan fuera del alcance de los lectores, que leen la etiqueta del
 * botón, y del catálogo de idiomas, que no tiene nada que decir sobre ellos.
 */
function MenosUno(): React.ReactElement {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
      <path d="M2 7h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function MasUno(): React.ReactElement {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
      <path d="M2 7h10M7 2v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Mostrador: la pantalla del comercio minorista.
 *
 * Está armada alrededor de tres cosas que pasan en un mostrador de verdad.
 *
 * **El lector escribe donde esté el cursor.** Por eso el foco vuelve al buscador después de
 * cada agregado y no hay ningún campo que se lo robe. Un código de barras que aterriza en el
 * campo de cantidad ensucia la venta sin que nadie entienda por qué.
 *
 * **La cuenta del vuelto la hace el sistema.** Se escribe con cuánto paga el cliente y el
 * vuelto aparece grande. Es la operación que más se hace mal a mano y la que más discusiones
 * genera.
 *
 * **Equivocarse es barato.** Quitar una línea es un botón, cancelar la venta entera es otro, y
 * nada se registra hasta que se cobra.
 */
export function Mostrador(): React.ReactElement {
  const { t } = useTranslation();
  const clienteDeConsultas = useQueryClient();
  const textoDeError = useTextoDeError();

  const [puestoId, setPuestoId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [ultima, setUltima] = useState<{
    comprobanteId: string;
    numero: string;
    /** La que emitió el servidor, no la que la pantalla había anticipado. */
    clase: string;
    total: string;
    vuelto: string;
    tokenPublico: string;
  } | null>(null);
  // Anular se ofrece **sólo sobre la venta que se acaba de hacer**, que es cuando se nota el
  // error. Buscar una vieja para anularla es otra pantalla y otra conversación.
  const [anulando, setAnulando] = useState(false);
  const [motivoDeAnulacion, setMotivo] = useState('');
  const puedeAnular = usePermiso('VENTAS.COMPROBANTE.ANULAR');

  const puestos = useQuery({
    queryKey: ['puestos-de-caja'],
    queryFn: () => pedir<Puesto[]>('/puestos-de-caja'),
  });
  const puntos = useQuery({
    queryKey: ['puntos-de-venta'],
    queryFn: () => pedir<PuntoDeVenta[]>('/puntos-de-venta'),
  });
  const listas = useQuery({
    queryKey: ['listas-de-precios'],
    queryFn: () => pedir<Lista[]>('/listas-de-precios'),
  });

  const puntoDeVentaId = puntos.data?.[0]?.id ?? '';

  /**
   * Con qué tipo se emite.
   *
   * **El selector aparece sólo si hay más de uno encendido.** Un minorista que emite tickets y
   * nada más no tiene por qué ver un desplegable de una opción en la pantalla que usa ocho horas;
   * el mayorista que además despacha con remito, sí.
   */
  const tiposDisponibles = useQuery({
    queryKey: ['tipos-de-comprobante'],
    queryFn: () => pedir<TipoDeComprobante[]>('/tipos-de-comprobante'),
  });
  /**
   * Lo que se puede emitir **desde el mostrador**.
   *
   * Quedan afuera la devolución y la nota de crédito aunque estén encendidas: no se emiten
   * vendiendo, se emiten desde Devoluciones a partir de un comprobante que existe. Ofrecerlas acá
   * invita a un error que después hay que anular.
   */
  const encendidos = (tiposDisponibles.data ?? []).filter(
    (tipo) =>
      tipo.activo &&
      tipo.familiaFiscal !== 'devolucion' &&
      tipo.familiaFiscal !== 'nota_de_credito' &&
      // **Los que exigen documento previo no se ofrecen acá**: se emiten desde Facturar entregas,
      // naciendo de lo que ya salió. Ofrecerlos sería invitar a un error que el servidor rechaza.
      !tipo.requiereOrigen &&
      // Ni los que el rol no tiene habilitados. Esconder no es la defensa —el servidor rechaza
      // igual— es cortesía: un menú con opciones que dan error enseña a desconfiar de la pantalla.
      tipo.puedeUsar,
  );
  const [tipoElegido, setTipoElegido] = useState<string>('');
  const tipoActual =
    encendidos.find((tipo) => tipo.id === tipoElegido) ??
    encendidos.find((tipo) => tipo.familiaFiscal === 'ticket_interno');
  const cobra = tipoActual === undefined || tipoActual.tocaCaja || tipoActual.afectaCuenta;

  /**
   * En qué caja incide esta venta.
   *
   * **El tipo manda si la fija.** Con una caja declarada, quien vende no la elige: el selector
   * queda bloqueado y a la vista —no escondido, porque saber en qué caja está cobrando es parte de
   * lo que la persona necesita ver—. Es la misma forma que la lista de precios fija de E24.
   */
  const puestoDelTipo = tipoActual?.puestoDeCajaId ?? null;
  const puestoActivo =
    puestoDelTipo ?? (puestoId === '' ? (puestos.data?.[0]?.id ?? '') : puestoId);

  const turno = useQuery({
    queryKey: ['turno-abierto', puestoActivo],
    enabled: puestoActivo !== '',
    queryFn: () => pedir<{ id: string } | null>(`/puestos-de-caja/${puestoActivo}/turno-abierto`),
  });

  const [cliente, setCliente] = useState<ClienteElegido | null>(null);

  /**
   * Con qué lista se le va a cobrar (E36). La decide el servidor con la misma cadena que usa la
   * venta —tipo fijo, cliente, tipo, caja, sucursal, comercio— y se vuelve a preguntar cada vez
   * que cambia alguno de los tres: el precio que se ve es el que se cobra.
   */
  const listaQueCorresponde = useQuery({
    queryKey: ['lista-que-corresponde', cliente?.id ?? '', tipoElegido, puestoActivo],
    enabled: puestoActivo !== '',
    queryFn: () =>
      pedir<{ id: string; nombre: string; origen: string }>(
        `/ventas/lista-que-corresponde?puestoDeCajaId=${puestoActivo}` +
          (cliente === null ? '' : `&terceroId=${cliente.id}`) +
          (tipoElegido === '' ? '' : `&tipoId=${tipoElegido}`),
      ),
  });
  const listaId =
    listaQueCorresponde.data?.id ?? listas.data?.find((lista) => lista.esPredeterminada)?.id;
  const escalas = listas.data?.find((lista) => lista.id === listaId)?.escalas ?? [];
  const venta = useVenta(listaId, escalas);
  // Cuando el límite frena la venta, la pantalla ofrece autorizar en vez de mandar a la persona
  // a buscar a alguien: quien puede autorizar suele estar a dos metros, no en otra pantalla.
  const [seVaDelLimite, setSeVaDelLimite] = useState(false);
  const puedeAutorizarCredito = usePermiso('VENTAS.CREDITO.AUTORIZAR');

  const faltaEfectivo =
    venta.medioDePago === 'efectivo' && venta.entregado.trim() !== '' && venta.vuelto.esNegativo();

  /**
   * El reparto entre formas de pago no cierra.
   *
   * El servidor lo rechaza igual —exige que la suma dé el total exacto—, pero descubrirlo al
   * apretar «Cobrar», con el cliente esperando, es tarde. El botón se apaga y el número que falta
   * ya está a la vista al lado.
   */
  const repartoNoCierra = cobra && !venta.faltante.esCero();

  const anular = useMutation({
    mutationFn: () =>
      pedir<{ numero: string }>(`/ventas/${ultima?.comprobanteId ?? ''}/anulacion`, {
        method: 'POST',
        cuerpo: { motivo: motivoDeAnulacion.trim() },
      }),
    onSuccess: async () => {
      // El panel de la última venta desaparece: ya no hay nada que anular ni ticket que valga.
      setUltima(null);
      setAnulando(false);
      setMotivo('');
      setError(null);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['existencias'] });
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  async function cobrar(autorizando = false): Promise<void> {
    if (venta.lineas.length === 0 || puntoDeVentaId === '' || puestoActivo === '') return;
    if (venta.medioDePago === 'cuenta_corriente' && cliente === null) {
      setError(t('venta.necesitaCliente'));
      return;
    }
    try {
      const vueltoDeEstaVenta = venta.vuelto;
      const resultado = await venta.emitir({
        puntoDeVentaId,
        puestoDeCajaId: puestoActivo,
        terceroId: cliente?.id,
        autorizarExcesoDeCredito: autorizando,
        ...(tipoElegido === '' ? {} : { tipoId: tipoElegido }),
        cobra,
      });
      setUltima({
        comprobanteId: resultado.comprobanteId,
        numero: resultado.numero,
        clase: resultado.clase,
        total: resultado.total,
        vuelto: vueltoDeEstaVenta.esPositivo() ? vueltoDeEstaVenta.aTexto() : '0',
        tokenPublico: resultado.tokenPublico,
      });
      setError(null);
      setSeVaDelLimite(false);
      venta.limpiar();
      // El cliente se limpia con la venta: la que viene es de otra persona hasta que alguien
      // diga lo contrario. Dejarlo puesto es cómo se termina facturando a nombre del anterior.
      setCliente(null);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['existencias'] });
    } catch (fallo: unknown) {
      setSeVaDelLimite(
        fallo instanceof ErrorDeApi && fallo.codigo === 'ERR_LIMITE_DE_CREDITO_SUPERADO',
      );
      setError(textoDeError(fallo));
    }
  }

  // F2 cobra. Es la tecla que ya tienen en la memoria los dedos de quien vino de otro sistema,
  // y evita que haya que soltar el teclado para llegar al botón.
  useEffect(() => {
    function alPresionar(evento: KeyboardEvent): void {
      if (evento.key === 'F2') {
        evento.preventDefault();
        void cobrar();
      }
    }
    window.addEventListener('keydown', alPresionar);
    return () => {
      window.removeEventListener('keydown', alPresionar);
    };
  });

  if (turno.isSuccess && turno.data === null) {
    return (
      <>
        <Encabezado titulo={t('venta.mostrador')} descripcion={t('venta.descripcionMostrador')} />
        <SinTurno />
      </>
    );
  }

  return (
    <>
      <Encabezado
        titulo={t('venta.mostrador')}
        descripcion={t('venta.descripcionMostrador')}
        acciones={
          <div className="w-56">
            <Selector
              etiqueta={t('venta.puesto')}
              value={puestoActivo}
              // Bloqueado y a la vista, no escondido: saber en qué caja está cobrando es parte de
              // lo que la persona necesita ver, aunque no pueda cambiarla.
              disabled={puestoDelTipo !== null}
              ayuda={puestoDelTipo === null ? undefined : t('venta.cajaFijadaPorElTipo')}
              onChange={(evento) => {
                setPuestoId(evento.target.value);
              }}
            >
              {(puestos.data ?? []).map((puesto) => (
                <option key={puesto.id} value={puesto.id}>
                  {puesto.nombre}
                </option>
              ))}
            </Selector>
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_22rem]">
        <div className="flex flex-col gap-4">
          <Tarjeta>
            <div className="mb-3">
              <SelectorDeCliente cliente={cliente} onElegir={setCliente} />
              {listaQueCorresponde.data === undefined ? null : (
                <p className="mt-1 text-apoyo text-texto-tenue">
                  {t('venta.conLaLista', { lista: listaQueCorresponde.data.nombre })}
                </p>
              )}
            </div>
            <BuscadorDeArticulos
              listaDePreciosId={listaId}
              etiqueta={t('venta.buscar')}
              onElegir={(variante) => {
                if (variante.precio === null) {
                  setError(t('venta.sinPrecioEnLista', { codigo: variante.codigo }));
                  return;
                }
                venta.agregar(variante);
                setError(null);
              }}
            />
          </Tarjeta>

          <Tarjeta>
            {venta.lineas.length === 0 ? (
              <p className="py-8 text-center text-sm text-texto-tenue">{t('venta.vacia')}</p>
            ) : (
              <ul className="flex flex-col divide-y divide-borde">
                {venta.lineas.map((linea) => {
                  const calculo = calcularLineaSegura(linea, escalas);
                  return (
                    <li key={linea.clave} className="flex items-center gap-3 py-2">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm">{linea.nombre}</p>
                        <p className="numero text-xs text-texto-tenue">
                          {linea.codigo}
                          {' · '}
                          {conDosDecimales(linea.precioUnitario)}
                        </p>
                      </div>

                      {/* Los botones grandes son para el dedo: en un mostrador con el lector en
                          una mano, el mouse fino no es una opción. */}
                      <div className="flex items-center gap-1">
                        <Boton
                          aria-label={`${t('venta.menosUno')} ${linea.codigo}`}
                          onClick={() => {
                            const nueva = Number(linea.cantidad) - 1;
                            if (nueva <= 0) venta.quitar(linea.clave);
                            else venta.cambiar(linea.clave, { cantidad: String(nueva) });
                          }}
                        >
                          <MenosUno />
                        </Boton>
                        <span className="numero w-12 text-center text-sm">{linea.cantidad}</span>
                        <Boton
                          aria-label={`${t('venta.masUno')} ${linea.codigo}`}
                          onClick={() => {
                            venta.cambiar(linea.clave, {
                              cantidad: String(Number(linea.cantidad) + 1),
                            });
                          }}
                        >
                          <MasUno />
                        </Boton>
                      </div>

                      <span className="numero w-28 text-right text-sm">
                        {importeVisible(calculo.importe)}
                      </span>

                      <Boton
                        tono="sutil"
                        aria-label={`${t('venta.quitar')} ${linea.codigo}`}
                        onClick={() => {
                          venta.quitar(linea.clave);
                        }}
                      >
                        {t('venta.quitar')}
                      </Boton>
                    </li>
                  );
                })}
              </ul>
            )}
          </Tarjeta>
        </div>

        <div className="flex flex-col gap-4">
          {encendidos.length < 2 ? null : (
            <Tarjeta>
              <Selector
                etiqueta={t('venta.tipoDeComprobante')}
                // El valor que se muestra es **el que se va a usar**, no el estado a secas: con el
                // estado vacío el desplegable caía en la primera opción de la lista y mostraba un
                // documento distinto del que el servidor iba a emitir.
                value={tipoActual?.id ?? ''}
                onChange={(evento) => {
                  setTipoElegido(evento.target.value);
                }}
              >
                {encendidos.map((tipo) => (
                  <option key={tipo.id} value={tipo.id}>
                    {tipo.nombre}
                  </option>
                ))}
              </Selector>
            </Tarjeta>
          )}

          <Tarjeta>
            <ResumenDeVenta totales={venta.totales} />
          </Tarjeta>

          <Tarjeta titulo={cobra ? t('venta.medioDePago') : t('venta.sinCobro')}>
            {/* Un documento que no cobra no pregunta cómo se paga: para un remito, la pregunta no
                tiene respuesta. El botón de emitir queda igual, que es lo que no puede faltar. */}
            <div className={cobra ? '' : 'hidden'}>
              <FormasDePago
                medios={MEDIOS}
                pagos={venta.pagos}
                faltante={venta.faltante}
                onElegirUnico={venta.setMedioDePago}
                onAgregar={venta.agregarPago}
                onCambiar={venta.cambiarPago}
                onQuitar={venta.quitarPago}
              />
            </div>

            {cobra && venta.medioDePago === 'efectivo' ? (
              <div className="mt-3 flex flex-col gap-1">
                <label className="text-xs font-medium text-texto-tenue" htmlFor="entregado">
                  {t('venta.entregado')}
                </label>
                <input
                  id="entregado"
                  inputMode="decimal"
                  className="numero rounded-marco border border-borde bg-superficie px-3 py-2 text-right text-lg"
                  value={venta.entregado}
                  onChange={(evento) => {
                    venta.setEntregado(evento.target.value);
                  }}
                />
                {venta.entregado.trim() === '' ? null : (
                  <p className="flex justify-between text-sm">
                    <span className="text-texto-tenue">
                      {venta.vuelto.esNegativo() ? t('venta.falta') : t('venta.vuelto')}
                    </span>
                    <span
                      className={`numero text-xl font-semibold ${
                        venta.vuelto.esNegativo() ? 'text-mal' : 'text-bien'
                      }`}
                    >
                      {importeVisible(venta.vuelto.valorAbsoluto())}
                    </span>
                  </p>
                )}
              </div>
            ) : null}

            <div className="mt-4 flex flex-col gap-2">
              {/* Con el efectivo declarado corto, cobrar queda bloqueado. Decir "Falta 7.857"
                  y a la vez dejar cobrar es una trampa: en un mostrador con gente esperando,
                  alguien va a apretar el botón igual. Quien no quiera usar la ayuda deja el
                  campo vacío y cobra normalmente. */}
              <Boton
                tono="principal"
                cargando={venta.emitiendo}
                disabled={venta.lineas.length === 0 || (cobra && faltaEfectivo) || repartoNoCierra}
                onClick={() => {
                  void cobrar();
                }}
              >
                {cobra ? t('venta.cobrar') : t('venta.emitir')}
              </Boton>
              <Boton
                tono="sutil"
                disabled={venta.lineas.length === 0}
                onClick={() => {
                  venta.limpiar();
                }}
              >
                {t('venta.cancelar')}
              </Boton>
            </div>

            <p className="mt-3 text-xs text-texto-tenue">{t('venta.atajos')}</p>

            {error === null ? null : (
              <p role="alert" className="mt-2 text-xs text-mal">
                {error}
              </p>
            )}

            {/* Frenar sin ofrecer salida manda a alguien a buscar a un encargado con el cliente
                esperando. Quien puede autorizar lo hace desde acá; quien no, ve por qué no. */}
            {seVaDelLimite ? (
              puedeAutorizarCredito ? (
                <Boton
                  className="mt-2 w-full"
                  tono="secundario"
                  cargando={venta.emitiendo}
                  onClick={() => {
                    void cobrar(true);
                  }}
                >
                  {t('venta.autorizarExceso')}
                </Boton>
              ) : (
                <p className="mt-2 text-xs text-texto-tenue">{t('venta.excesoNoAutorizado')}</p>
              )
            ) : null}

            {ultima === null ? null : (
              <div role="status" className="mt-3 rounded-marco border border-bien p-2 text-sm">
                <p className="text-bien">
                  {t('venta.cobrada', {
                    clase: ultima.clase,
                    numero: ultima.numero,
                    total: conDosDecimales(ultima.total),
                  })}
                </p>
                {Number(ultima.vuelto) > 0 ? (
                  <p className="numero">
                    {t('venta.vueltoDe', { importe: conDosDecimales(ultima.vuelto) })}
                  </p>
                ) : null}
                {/* El ticket se abre en otra pestaña: la venta siguiente ya puede empezar
                    mientras la impresión espera. */}
                <a
                  className="mt-1 inline-block text-acento underline"
                  href={`/t/${ultima.tokenPublico}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('ticket.verTicket')}
                </a>

                {puedeAnular ? (
                  anulando ? (
                    <div className="mt-2 flex flex-col gap-2">
                      <input
                        aria-label={t('venta.motivoDeAnulacion')}
                        placeholder={t('venta.motivoDeAnulacion')}
                        maxLength={200}
                        className="rounded-marco border border-borde bg-superficie px-2 py-1 text-sm"
                        value={motivoDeAnulacion}
                        onChange={(evento) => {
                          setMotivo(evento.target.value);
                        }}
                      />
                      <div className="flex gap-2">
                        <Boton
                          tono="peligro"
                          disabled={motivoDeAnulacion.trim() === '' || anular.isPending}
                          cargando={anular.isPending}
                          onClick={() => {
                            anular.mutate();
                          }}
                        >
                          {t('venta.confirmarAnulacion')}
                        </Boton>
                        <Boton
                          tono="secundario"
                          onClick={() => {
                            setAnulando(false);
                            setMotivo('');
                          }}
                        >
                          {t('acciones.cancelar')}
                        </Boton>
                      </div>
                    </div>
                  ) : (
                    <Boton
                      className="mt-2"
                      tono="secundario"
                      onClick={() => {
                        setAnulando(true);
                      }}
                    >
                      {t('venta.anular')}
                    </Boton>
                  )
                ) : null}
              </div>
            )}
          </Tarjeta>
        </div>
      </div>
    </>
  );
}
